/**
 * Usage-rights wording for the Audio Studio. Legal edits this copy here.
 *
 * Rules (client launch, Nov 2026):
 * - The app never says a sound is cleared for paid ads unless the track carries a licence record from the API
 *   (`license_record`, see `hasPaidAdsLicense`). Studio sounds are AI-generated and say so on the row, in the tooltip
 *   and in the downloaded file's name.
 * - "Cleared for TikTok ads" (and the "TikTok only" chip) only for a trend whose commercial flag comes from TikTok's
 *   official Commercial Music Library API (`AudioTrend.cml_cleared`). Scraped or sample trend lists make no claim.
 * - No blanket claims ("every sound is cleared ...").
 */
import { hasPaidAdsLicense, trackLicense, type StudioTrack } from '@/api/audioStudioApi';

/** Shown on every studio sound (rows, tooltips, downloads). */
export const AI_LABEL = 'AI-generated';

export const RIGHTS = {
  tiktok: {
    label: 'TikTok only',
    tooltip: 'Licensed for TikTok business use via TikTok’s Commercial Music Library. For other platforms, make an AI original in its style with “Remix”.',
  },
  original: {
    label: 'Original',
    tooltip: 'Generated with AI for your brand. Check your paid ads terms before running it as an ad.',
  },
} as const;

export type RightsKind = keyof typeof RIGHTS;

/** What one studio sound may be used for: the licence when it has one, else the plain AI-generated line. */
export function trackRightsText(t: Pick<StudioTrack, 'license_record' | 'license'> | null | undefined): string {
  if (!hasPaidAdsLicense(t)) return RIGHTS.original.tooltip;
  const platforms = trackLicense(t)?.platforms?.filter(Boolean) || [];
  return `Generated with AI for your brand. Licensed for paid ads${platforms.length ? ` on ${platforms.join(', ')}` : ''}.`;
}

/** The file name a studio sound downloads as: its title, marked as AI-generated. */
export function aiDownloadName(title: string | null | undefined): string {
  return `${(title || 'sound').trim() || 'sound'} (${AI_LABEL})`;
}

/** Said once above the team's sounds (Recent creations, Your Collections) instead of a chip on every row. */
export const ORIGINALS_NOTE = 'Your sounds are AI-generated for your brand. Check your paid ads terms before running one as an ad.';

/** Above the trends list when it comes from TikTok's official Commercial Music Library API. */
export const TRENDS_RIGHTS_NOTE = 'TikTok’s Commercial Music Library: cleared for TikTok ads. Make an AI original in a song’s style for other platforms.';

/** Above a trends list from any other source (scraped or sample): what is trending, no licence claim. */
export const TRENDS_NO_CLAIM_NOTE = 'What is trending on TikTok. No ad licence is confirmed for these songs. Make an AI original in a song’s style instead.';

/** The trends note for a list: the TikTok claim only when the list came from TikTok's official API. */
export function trendsRightsNote(source: string | null | undefined): string {
  return source === 'tiktok_cml' ? TRENDS_RIGHTS_NOTE : TRENDS_NO_CLAIM_NOTE;
}

/** One trend's licence line (song drawer): the TikTok licence only when the trend carries the official flag. */
export const TREND_NO_CLAIM_TEXT = 'No licence is confirmed for this song. Do not use it in ads; make an AI original in its style instead.';
export function trendRightsText(t: { cml_cleared?: boolean } | null | undefined): string {
  return t?.cml_cleared === true ? RIGHTS.tiktok.tooltip : TREND_NO_CLAIM_TEXT;
}

/** One line in the Remix dialog on what Remix makes. Without a licence record a remix is for organic use only. */
export const REMIX_NOTE = 'Remix makes an AI-generated original in this song’s style (tempo, instruments, energy), for organic use only.';

/** Remix dialog, first rights line, when the trend has no official TikTok licence flag. */
export const REMIX_SONG_NO_CLAIM = { label: 'The trending song', text: "no ad licence is confirmed for it. Your remix doesn't reuse it." } as const;

/** The remix rights lines for one trend: the TikTok licence line only when the trend carries the official flag. */
export function remixRightsItems(t: { cml_cleared?: boolean } | null | undefined): ReadonlyArray<{ label: string; text: string }> {
  return t?.cml_cleared === true ? REMIX_RIGHTS.items : [REMIX_SONG_NO_CLAIM, ...REMIX_RIGHTS.items.slice(1)];
}

/** Remix dialog: what the user may do with the remix (always visible). */
export const REMIX_RIGHTS = {
  title: 'Rights for this remix',
  items: [
    { label: 'The trending song', text: "licensed for TikTok business use only (TikTok Commercial Music Library). Your remix doesn't reuse it." },
    { label: 'Your remix', text: "a new, AI-generated composition in the song's style. No melody, lyrics or audio from the original are copied." },
    { label: 'Organic use only', text: 'your brand and your creators can use it in organic posts. Check your paid ads terms before running it as an ad.' },
  ],
} as const;

/** Remix dialog: how a remix is made and where it goes next (collapsible). */
export const REMIX_HOW_IT_WORKS = {
  title: 'How it works',
  steps: [
    'We listen to the trending song and map its style: tempo, key and which instruments play in each bar.',
    'We compose a new original from that map plus your instructions with AI (about a minute).',
    'It appears in Recent creations: play it, rename it, share it.',
    "Add it to a beat or a creator's brief, or download it and send it to the creator.",
  ],
} as const;

export const MUSIC_RIGHTS_TERMS_LABEL = 'Music rights terms';

/**
 * The licence on a sound's public page (/sound/:id, /share/sound/:id) — what anyone opening the link (creators on a
 * brief, brands, partners) may do with it. Deliberately not brand-specific: sounds come from BO, Omni and later the
 * creator app. No paid ads claim: the public page has no licence record to back one.
 */
export const PUBLIC_SOUND_LICENSE = {
  /** The one line always shown; the items open from "Licensing terms". */
  summary: 'AI-generated music made with Beatly: for organic posts in Beatly campaigns.',
  title: 'Licensing terms',
  items: [
    'Original music generated with AI by Beatly: no melody, lyrics or recording is copied from an existing song.',
    'For content made for Beatly campaigns: organic posts on TikTok, Instagram, YouTube and other platforms.',
    'Paid ads only where the brand’s agreement with Beatly covers it. Ask the brand before running it as an ad.',
    'Creators working on a Beatly brief may download it and use it in that content.',
    'Not for resale, distribution as standalone music, or registration with Content ID or other rights-management services.',
  ],
} as const;
